import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

function AdminPanel() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [newItem, setNewItem] = useState({ name: '', price: '', category: '', description: '', icon: '', hidden: false });
  const [newCategory, setNewCategory] = useState({ name: '', icon: '', order: '', hidden: false });
  const [editItem, setEditItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryDraft, setEditCategoryDraft] = useState({ name: '', icon: '', order: 0 });
  const [, forceUpdate] = useState({});

  // Fetch menu items and categories from Firestore
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch menu items
        const menuSnapshot = await getDocs(collection(db, 'menu'));
        const items = menuSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMenuItems(items);
        // Fetch categories
        const catSnapshot = await getDocs(collection(db, 'categories'));
        const cats = catSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(cats);
      } catch (err) {
        setError('Failed to load menu or categories.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      forceUpdate({});
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {}, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      setTimeout(() => forceUpdate({}), 100);
    } catch (err) {
      setLoginError('Invalid credentials.');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleInputChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  // Category add
  const handleNewCategoryChange = (e) => {
    setNewCategory({ ...newCategory, [e.target.name]: e.target.value });
  };
  const handleAddCategory = async (e) => {
    e.preventDefault();
    const name = newCategory.name.trim();
    const icon = newCategory.icon.trim();
    const order = parseInt(newCategory.order) || 0;
    
    if (!name || !icon) {
      alert('Please fill in both name and icon');
      return;
    }

    if (categories.some(cat => cat.name === name)) {
      alert('Category already exists!');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'categories'), { name, icon, order, hidden: newCategory.hidden || false });
      setCategories([...categories, { id: docRef.id, name, icon, order, hidden: newCategory.hidden || false }]);
      setNewCategory({ name: '', icon: '', order: '', hidden: false });
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };
  const handleDeleteCategory = async (categoryToDelete) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryToDelete.name}"? This will also delete all items in this category.`)) {
      return;
    }
    try {
      // Delete all items in this category
      const itemsToDelete = menuItems.filter(item => item.category === categoryToDelete.name);
      for (const item of itemsToDelete) {
        await deleteDoc(doc(db, 'menu', item.id));
      }
      // Delete category from Firestore
      await deleteDoc(doc(db, 'categories', categoryToDelete.id));
      // Remove from state
      setMenuItems(menuItems.filter(item => item.category !== categoryToDelete.name));
      setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
      if (editItem && editItem.category === categoryToDelete.name) {
        setEditItem(null);
      }
      alert(`Category "${categoryToDelete.name}" and all its items have been deleted.`);
    } catch (err) {
      alert('Failed to delete category.');
    }
  };
  // Edit category
  const handleEditCategoryClick = (cat) => {
    setEditingCategory(cat);
    setEditCategoryDraft({ name: cat.name, icon: cat.icon, order: cat.order || 0 });
  };
  const handleEditCategoryDraftChange = (e) => {
    setEditCategoryDraft({ ...editCategoryDraft, [e.target.name]: e.target.value });
  };
  const handleSaveEditCategory = async (oldCat) => {
    const newName = editCategoryDraft.name.trim();
    const newIcon = editCategoryDraft.icon.trim();
    const newOrder = parseInt(editCategoryDraft.order) || 0;
    
    if (!newName || !newIcon) {
      alert('Please fill in both name and icon');
      return;
    }

    try {
      // Update category in Firestore
      await updateDoc(doc(db, 'categories', oldCat.id), {
        name: newName,
        icon: newIcon,
        order: newOrder,
        hidden: oldCat.hidden || false
      });

      // Update local state
      setCategories(categories.map(cat =>
        cat.id === oldCat.id 
          ? { ...cat, name: newName, icon: newIcon, order: newOrder, hidden: oldCat.hidden || false }
          : cat
      ));

      setEditingCategory(null);
      setEditCategoryDraft({ name: '', icon: '', order: 0 });
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };
  const handleCancelEditCategory = () => {
    setEditingCategory(null);
  };

  // Item add/edit
  const handleNewItemChange = (e) => {
    setNewItem({ ...newItem, [e.target.name]: e.target.value });
  };
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name || !newItem.price || !newItem.category) return;
    const price = parseFloat(newItem.price);
    if (isNaN(price)) return;
    try {
      const docRef = await addDoc(collection(db, 'menu'), {
        name: newItem.name,
        price,
        category: newItem.category,
        description: newItem.description,
        icon: newItem.icon || '',
        hidden: newItem.hidden || false,
      });
      const newItemWithId = { id: docRef.id, ...newItem, price };
      setMenuItems([...menuItems, newItemWithId]);
      setNewItem({ name: '', price: '', category: '', description: '', icon: '', hidden: false });
    } catch (err) {
      alert('Failed to add item.');
    }
  };
  const handleEditItem = (item) => {
    setEditItem(item);
  };
  const handleEditChange = (e) => {
    setEditItem({ ...editItem, [e.target.name]: e.target.value });
  };
  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editItem.name || !editItem.price || !editItem.category) return;
    const price = parseFloat(editItem.price);
    if (isNaN(price)) return;
    try {
      await updateDoc(doc(db, 'menu', editItem.id), {
        name: editItem.name,
        price,
        category: editItem.category,
        description: editItem.description,
        icon: editItem.icon || '',
        hidden: editItem.hidden || false,
      });
      setMenuItems(menuItems.map(item => item.id === editItem.id ? { ...editItem, price } : item));
      setEditItem(null);
    } catch (err) {
      alert('Failed to update item.');
    }
  };
  const handleDeleteItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'menu', id));
        setMenuItems(menuItems.filter(item => item.id !== id));
      } catch (err) {
        alert('Failed to delete item.');
      }
    }
  };

  const handleToggleItemVisibility = async (item) => {
    try {
      const newHiddenStatus = !item.hidden;
      await updateDoc(doc(db, 'menu', item.id), {
        hidden: newHiddenStatus
      });
      setMenuItems(menuItems.map(menuItem => 
        menuItem.id === item.id 
          ? { ...menuItem, hidden: newHiddenStatus }
          : menuItem
      ));
    } catch (err) {
      alert('Failed to update item visibility.');
    }
  };

  const handleToggleCategoryVisibility = async (category) => {
    try {
      const newHiddenStatus = !category.hidden;
      await updateDoc(doc(db, 'categories', category.id), {
        hidden: newHiddenStatus
      });
      setCategories(categories.map(cat => 
        cat.id === category.id 
          ? { ...cat, hidden: newHiddenStatus }
          : cat
      ));
    } catch (err) {
      alert('Failed to update category visibility.');
    }
  };

  if (authLoading) {
    return (
      <div className="App summer-theme-bg">
        <header className="App-header">
          <h1 className="summer-title">Admin Panel</h1>
          <p className="subtitle">Loading...</p>
        </header>
      </div>
    );
  }

  return (
    <div className="App summer-theme-bg">
      <header className="App-header">
        <h1 className="summer-title">Admin Panel</h1>
        <p className="subtitle">Manage Menu Items</p>
        <Link to="/" className="back-link">← Back to Menu</Link>
      </header>
      <main>
        <section className="menu-section">
          <h2 className="menu-title">Menu Management</h2>
          <div className="menu-list">
            {loading && <p>Loading menu...</p>}
            {error && <p className="error">{error}</p>}
            {!loading && !error && (() => {
              // Group items by category and sort categories by order
              const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
              const itemsByCategory = {};
              
              // Group items by category
              menuItems.forEach(item => {
                if (!itemsByCategory[item.category]) {
                  itemsByCategory[item.category] = [];
                }
                itemsByCategory[item.category].push(item);
              });
              
              // Sort items within each category by price
              Object.keys(itemsByCategory).forEach(category => {
                itemsByCategory[category].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
              });
              
              return sortedCategories.map(category => {
                const categoryItems = itemsByCategory[category.name] || [];
                if (categoryItems.length === 0) return null;
                
                return (
                  <div key={category.id} className="category-section-admin" style={{marginBottom: '2rem'}}>
                    <h3 style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#ff7043',
                      fontSize: '1.5rem',
                      marginBottom: '1rem',
                      padding: '0.5rem 1rem',
                      background: '#fff176',
                      borderRadius: '1rem',
                      border: '2px solid #39ff14'
                    }}>
                      <span style={{fontSize: '1.8rem'}}>{category.icon || '❓'}</span>
                      {category.name}
                      <span style={{fontSize: '0.9rem', color: '#666', marginLeft: 'auto'}}>
                        ({categoryItems.length} items)
                      </span>
                    </h3>
                    <div className="category-items" style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'}}>
                      {categoryItems.map(item => (
                        editItem && editItem.id === item.id ? (
                          <form key={item.id} className="menu-item summer-card" style={{background: '#fff176', color: '#ff7043', gap: '0.5rem', display: 'flex', flexDirection: 'column'}} onSubmit={handleUpdateItem}>
                            <input name="name" value={editItem.name} onChange={handleEditChange} required style={{fontWeight: 700, fontSize: '1.1rem', color: '#ff7043', borderRadius: '0.7rem', border: '1.5px solid #39ff14', padding: '0.5rem'}} />
                            <select name="category" value={editItem.category} onChange={handleEditChange} required style={{borderRadius: '0.7rem', border: '1.5px solid #39ff14', padding: '0.5rem', fontSize: '1rem'}}>
                              <option value="">Select Category</option>
                              {categories.map(category => (
                                <option key={category.id} value={category.name}>{category.icon ? `${category.icon} ` : ''}{category.name}</option>
                              ))}
                            </select>
                            <input name="price" value={editItem.price} onChange={handleEditChange} type="number" step="0.01" min="0" required style={{borderRadius: '0.7rem', border: '1.5px solid #39ff14', padding: '0.5rem', fontSize: '1rem'}} />
                            <textarea name="description" value={editItem.description} onChange={handleEditChange} style={{borderRadius: '0.7rem', border: '1.5px solid #39ff14', padding: '0.5rem', fontSize: '1rem'}} />
                            <input name="icon" value={editItem.icon || ''} onChange={handleEditChange} maxLength={2} placeholder="Emoji (optional)" style={{width: '3em', textAlign: 'center', fontSize: '1.5em', borderRadius: '0.7rem', border: '1.5px solid #39ff14', padding: '0.5rem'}} />
                            <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                              <button type="submit" style={{background: '#39ff14', color: '#fff', border: 'none', borderRadius: '0.7em', padding: '0.4em 1.2em', fontWeight: 600}}>Save</button>
                              <button type="button" onClick={() => setEditItem(null)} style={{background: '#ff4f70', color: '#fff', border: 'none', borderRadius: '0.7em', padding: '0.4em 1.2em', fontWeight: 600}}>Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <div key={item.id} className="menu-item summer-card" style={{
                            opacity: item.hidden ? 0.6 : 1,
                            border: item.hidden ? '2px dashed #ff7043' : '2px solid #39ff14'
                          }}>
                            <div className="item-header-row" style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '0.5rem'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                flex: 1
                              }}>
                                <span className="item-icon">{item.icon || '🍸'}</span>
                                <span className="item-name" style={{color: item.hidden ? '#999' : '#ff7043', fontWeight: 'bold'}}>
                                  {item.name} {item.hidden && <span style={{color: '#ff4f70', fontSize: '0.8em'}}>(Hidden)</span>}
                                </span>
                              </div>
                              <span className="item-price" style={{
                                color: '#ff7043',
                                fontWeight: 'bold',
                                fontSize: '1.1rem'
                              }}>
                                {parseFloat(item.price).toFixed(2)} TND
                              </span>
                            </div>
                            <div className="item-description">{item.description}</div>
                            {user && (
                              <div className="admin-controls">
                                <button onClick={() => handleEditItem(item)} className="edit-btn">Edit</button>
                                <button 
                                  onClick={() => handleToggleItemVisibility(item)} 
                                  style={{
                                    background: item.hidden ? '#39ff14' : '#ff4f70',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    padding: '0.3rem 0.7rem',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    marginRight: '0.5rem'
                                  }}
                                >
                                  {item.hidden ? 'Show' : 'Hide'}
                                </button>
                                <button onClick={() => handleDeleteItem(item.id)} className="delete-btn">Delete</button>
                              </div>
                            )}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </section>
        <section className="admin-section">
          {user ? (
            <div>
              <button onClick={handleLogout} className="logout-btn">Logout</button>
              {/* Category Management */}
              <div className="category-section">
                <h3>Manage Categories</h3>
                <form onSubmit={handleAddCategory} className="admin-form">
                  <input 
                    name="name" 
                    placeholder="New Category Name" 
                    value={newCategory.name} 
                    onChange={handleNewCategoryChange} 
                    required 
                  />
                  <input 
                    name="icon" 
                    placeholder="Emoji (e.g. 🥞)" 
                    value={newCategory.icon} 
                    onChange={handleNewCategoryChange} 
                    maxLength={2}
                    style={{width: '3em', textAlign: 'center', fontSize: '1.5em'}}
                  />
                  <input 
                    name="order" 
                    placeholder="Order (optional)" 
                    value={newCategory.order} 
                    onChange={handleNewCategoryChange} 
                    type="number" 
                    min="0" 
                    style={{width: '5em', fontSize: '1em'}}
                  />
                  <button type="submit">Add Category</button>
                </form>
                <div className="categories-list">
                  <h4>Existing Categories:</h4>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem'}}>
                    {categories.map(category => (
                      editingCategory && editingCategory.id === category.id ? (
                        <form key={category.id} style={{display: 'flex', alignItems: 'center', background: '#fff176', padding: '0.3rem 0.8rem', borderRadius: '1rem', fontSize: '0.9rem', color: '#ff7043', gap: '0.5rem'}} onSubmit={e => {e.preventDefault(); handleSaveEditCategory(category);}}>
                          <input name="icon" value={editCategoryDraft.icon} onChange={handleEditCategoryDraftChange} maxLength={2} style={{width: '2em', textAlign: 'center', fontSize: '1.2em'}} />
                          <input name="name" value={editCategoryDraft.name} onChange={handleEditCategoryDraftChange} style={{width: '6em', fontSize: '1em'}} />
                          <input name="order" value={editCategoryDraft.order} onChange={handleEditCategoryDraftChange} type="number" min="0" style={{width: '3em', fontSize: '1em'}} />
                          <button type="submit" style={{background: '#39ff14', color: '#fff', border: 'none', borderRadius: '0.7em', padding: '0.2em 0.7em', fontWeight: 600, marginLeft: '0.2em'}}>Save</button>
                          <button type="button" onClick={handleCancelEditCategory} style={{background: '#ff4f70', color: '#fff', border: 'none', borderRadius: '0.7em', padding: '0.2em 0.7em', fontWeight: 600, marginLeft: '0.2em'}}>Cancel</button>
                        </form>
                      ) : (
                        <div key={category.id} style={{
                          display: 'flex', 
                          alignItems: 'center', 
                          background: category.hidden ? '#f5f5f5' : '#fff176', 
                          padding: '0.3rem 0.8rem', 
                          borderRadius: '1rem', 
                          fontSize: '0.9rem', 
                          color: category.hidden ? '#999' : '#ff7043', 
                          gap: '0.5rem',
                          opacity: category.hidden ? 0.7 : 1,
                          border: category.hidden ? '1px dashed #ccc' : '1px solid #39ff14'
                        }}>
                          <span style={{fontSize: '1.3em'}}>{category.icon || '❓'}</span>
                          <span>{category.name} {category.hidden && <span style={{color: '#ff4f70', fontSize: '0.8em'}}>(Hidden)</span>}</span>
                          <button 
                            onClick={() => handleToggleCategoryVisibility(category)} 
                            style={{
                              background: category.hidden ? '#39ff14' : '#ff4f70',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              fontSize: '10px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }} 
                            title={category.hidden ? `Show category "${category.name}"` : `Hide category "${category.name}"`}
                          >
                            {category.hidden ? '👁️' : '🙈'}
                          </button>
                          <button onClick={() => handleEditCategoryClick(category)} style={{background: '#ffe082', color: '#ff7043', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'}} title={`Edit category "${category.name}"`}>
                            ✏️
                          </button>
                          <button onClick={() => handleDeleteCategory(category)} style={{background: 'linear-gradient(90deg, #ff4f70 60%, #f44336 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'}} title={`Delete category "${category.name}" and all its items`}>
                            ×
                          </button>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
              {/* Add New Menu Item */}
              <div className="add-item-section">
                <h3>Add New Menu Item</h3>
                <form onSubmit={handleAddItem} className="admin-form">
                  <input name="name" placeholder="Item Name" value={newItem.name} onChange={handleNewItemChange} required />
                  <select 
                    name="category" 
                    value={newItem.category} 
                    onChange={handleNewItemChange} 
                    required
                    style={{
                      padding: '1rem',
                      borderRadius: '0.7rem',
                      border: '2px solid #39ff14',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border 0.2s',
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.icon ? `${category.icon} ` : ''}{category.name}</option>
                    ))}
                  </select>
                  <input name="price" placeholder="Price in TND" value={newItem.price} onChange={handleNewItemChange} type="number" step="0.01" min="0" required />
                  <textarea name="description" placeholder="Description (optional)" value={newItem.description} onChange={handleNewItemChange} />
                  <input 
                    name="icon" 
                    placeholder="Emoji (optional)" 
                    value={newItem.icon} 
                    onChange={handleNewItemChange} 
                    maxLength={2}
                    style={{width: '3em', textAlign: 'center', fontSize: '1.5em'}}
                  />
                  <button type="submit">Add Item</button>
                </form>
              </div>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="login-form">
              <h3>Admin Login</h3>
              <input name="email" placeholder="Email Address" value={loginForm.email} onChange={handleInputChange} type="email" required />
              <input name="password" type="password" placeholder="Password" value={loginForm.password} onChange={handleInputChange} required />
              <button type="submit">Login</button>
              {loginError && <p className="error">{loginError}</p>}
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

export default AdminPanel; 